/**
 * The HTTP client for the AlohaMula API.
 *
 * Every mutating call carries an `expectedVersion`, because the scraper and the
 * iOS app are writing to the same tables. A 409 is not an error to retry blindly
 * — it means someone else changed the row and the user needs to see the new
 * value before deciding again.
 */

import type {
  Allocation,
  BudgetItem,
  BudgetItemKind,
  FundingSchedule,
  PaycheckSettings,
  Presence,
  Snapshot,
  SortingRule,
  Transaction,
} from '@alohamula/domain';
import { config } from '../config';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    readonly details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Someone else changed this row first. */
  get isConflict(): boolean {
    return this.status === 409;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

type TokenSource = () => Promise<string | null>;

let getToken: TokenSource = async () => null;

/** Wired up once by `<ApiProvider>` so modules can call the API without prop-drilling. */
export function setTokenSource(source: TokenSource): void {
  getToken = source;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal } = options;

  const url = new URL(`${config.apiEndpoint}${path}`);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined) url.searchParams.set(key, String(value));
  }

  const token = await getToken();
  if (!token) {
    throw new ApiError('Your session ended. Sign in again.', 401, 'UNAUTHORIZED');
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      signal,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(
      'Could not reach AlohaMula. Check your connection and try again.',
      0,
      'NETWORK_ERROR'
    );
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text ? safeParse(text) : undefined;

  if (!response.ok) {
    const error = (payload as { error?: { code?: string; message?: string; details?: Record<string, unknown> } })
      ?.error;
    throw new ApiError(
      error?.message ?? `Request failed (${response.status})`,
      response.status,
      error?.code ?? 'UNKNOWN',
      error?.details
    );
  }

  return payload as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

// ---------------------------------------------------------------------------
// Snapshot
// ---------------------------------------------------------------------------

/** The whole budget in one request. Cheaper than five round trips on a cold load. */
export function fetchSnapshot(signal?: AbortSignal): Promise<Snapshot> {
  return request<Snapshot>('/snapshot', { signal });
}

// ---------------------------------------------------------------------------
// Budget items
// ---------------------------------------------------------------------------

export interface CreateBudgetItemInput {
  kind: BudgetItemKind;
  name: string;
  icon: string;
  color: string;
  category: string;
  perPaycheckAmount: number;
  fundingSchedule?: FundingSchedule;
  isActive?: boolean;
  isFund?: boolean;
  isPinned?: boolean;
  dueDay?: number | null;
  notes?: string | null;
  sortOrder?: number;
  parentId?: string | null;
  expiresAt?: string | null;
}

export function createBudgetItem(input: CreateBudgetItemInput): Promise<BudgetItem> {
  return request<BudgetItem>('/budget-items', { method: 'POST', body: input });
}

export interface UpdateBudgetItemInput extends Partial<Omit<CreateBudgetItemInput, 'kind'>> {
  expectedVersion: number;
}

export function updateBudgetItem(id: string, input: UpdateBudgetItemInput): Promise<BudgetItem> {
  return request<BudgetItem>(`/budget-items/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: input,
  });
}

export function deleteBudgetItem(id: string): Promise<void> {
  return request<void>(`/budget-items/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// Transactions
// ---------------------------------------------------------------------------

export interface UpdateTransactionInput {
  description?: string;
  category?: string | null;
  isNew?: boolean;
  expectedVersion: number;
}

export function updateTransaction(
  id: string,
  input: UpdateTransactionInput
): Promise<Transaction> {
  return request<Transaction>(`/transactions/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: input,
  });
}

export function trashTransaction(
  id: string,
  expectedVersion: number,
  reason?: string
): Promise<Transaction> {
  return request<Transaction>(`/transactions/${encodeURIComponent(id)}/trash`, {
    method: 'POST',
    body: { expectedVersion, reason: reason ?? null },
  });
}

export function restoreTransaction(id: string, expectedVersion: number): Promise<Transaction> {
  return request<Transaction>(`/transactions/${encodeURIComponent(id)}/restore`, {
    method: 'POST',
    body: { expectedVersion },
  });
}

export interface TransactionInsight {
  merchant: string | null;
  relatedTo: string | null;
  suggestedBudgetItemId: string | null;
  suggestedBudgetItemName: string | null;
  suggestedKind: 'envelope' | 'bill' | null;
  confidence: number;
  summary: string;
  reasons: string[];
}

/** Looks up a cryptic bank line: who it might be, which envelope to file it under. */
export function fetchTransactionInsight(id: string, signal?: AbortSignal): Promise<TransactionInsight> {
  return request<TransactionInsight>(`/transactions/${encodeURIComponent(id)}/insight`, {
    method: 'POST',
    body: {},
    signal,
  });
}

// ---------------------------------------------------------------------------
// Chat
// ---------------------------------------------------------------------------

export interface ChatSession {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}

export interface ChatProposal {
  id: string;
  kind: string;
  summary: string;
  destructive: boolean;
  payload: Record<string, unknown>;
}

export interface ChatArtifact {
  id: string;
  title: string;
  markdown: string;
  period?: string | null;
}

export interface ChatMessage {
  id: string;
  role: string;
  content: string;
  toolStatus?: string | null;
  proposal?: ChatProposal | null;
  artifact?: ChatArtifact | null;
  createdAt: string;
}

export interface ChatTurnEvent {
  type: string;
  text?: string;
  name?: string;
  message?: string;
  assistantMessageId?: string;
  proposal?: ChatProposal;
  artifact?: ChatArtifact;
}

export function listChatSessions(): Promise<{ sessions: ChatSession[] }> {
  return request<{ sessions: ChatSession[] }>('/chat/sessions');
}

export function createChatSession(title?: string): Promise<ChatSession> {
  return request<ChatSession>('/chat/sessions', {
    method: 'POST',
    body: { title: title ?? 'New chat' },
  });
}

export function getChatMessages(sessionId: string): Promise<{ messages: ChatMessage[] }> {
  return request<{ messages: ChatMessage[] }>(
    `/chat/sessions/${encodeURIComponent(sessionId)}/messages`
  );
}

export function deleteChatSession(sessionId: string): Promise<void> {
  return request<void>(`/chat/sessions/${encodeURIComponent(sessionId)}`, { method: 'DELETE' });
}

export interface AssistantSuggestion {
  id: string;
  kind: string;
  title: string;
  body: string;
  dismissKey: string;
  actionLabel: string;
  chatPrompt: string;
  proposal: ChatProposal | null;
}

export interface AssistantSuggestionsResponse {
  suggestions: AssistantSuggestion[];
  generatedAt: string | null;
  stale: boolean;
}

export function listAssistantSuggestions(): Promise<AssistantSuggestionsResponse> {
  return request<AssistantSuggestionsResponse>('/assistant/suggestions');
}

export function refreshAssistantWatch(): Promise<AssistantSuggestionsResponse> {
  return request<AssistantSuggestionsResponse>('/assistant/watch', {
    method: 'POST',
    body: { force: true },
  });
}

export function dismissAssistantSuggestion(id: string): Promise<AssistantSuggestionsResponse> {
  return request<AssistantSuggestionsResponse>(
    `/assistant/suggestions/${encodeURIComponent(id)}/dismiss`,
    { method: 'POST', body: {} }
  );
}

export function postChatTurn(
  sessionId: string,
  message: string,
  prefillTransactionId?: string
): Promise<{ events: ChatTurnEvent[]; messages: ChatMessage[] }> {
  return request<{ events: ChatTurnEvent[]; messages: ChatMessage[] }>(
    `/chat/sessions/${encodeURIComponent(sessionId)}/turns`,
    {
      method: 'POST',
      body: { message, prefillTransactionId: prefillTransactionId ?? null },
    }
  );
}

/** Trashed transactions, which the snapshot deliberately leaves out. */
export interface CreateTransactionInput {
  transactionDate: string;
  description: string;
  amount: number;
  type: 'debit' | 'credit';
  source?: string;
  note?: string;
  status?: string;
}

export function createTransaction(input: CreateTransactionInput): Promise<Transaction> {
  return request<Transaction>('/transactions', { method: 'POST', body: input });
}

export async function borrowBetweenItems(input: {
  amount: number;
  fromId: string;
  fromName: string;
  toId: string;
  toName: string;
  date: string;
  note?: string;
}): Promise<void> {
  const amount = Math.round(input.amount * 100) / 100;
  if (!(amount > 0)) throw new ApiError('Enter how much to borrow.', 400, 'VALIDATION_ERROR');
  if (input.fromId === input.toId) {
    throw new ApiError('Pick a different envelope to borrow from.', 400, 'VALIDATION_ERROR');
  }
  const why = input.note?.trim() || `Cover ${input.toName}`;
  const taken = await createTransaction({
    transactionDate: input.date,
    description: `Borrowed by ${input.toName} — ${why}`,
    amount: -amount,
    type: 'debit',
    source: 'manual',
    note: `Borrowed by ${input.toName} — ${why}`,
    status: 'posted',
  });
  await setAllocations(taken.id, [{ budgetItemId: input.fromId, amount }], taken.version);
  const given = await createTransaction({
    transactionDate: input.date,
    description: `Borrowed from ${input.fromName} — ${why}`,
    amount,
    type: 'credit',
    source: 'manual',
    note: `Borrowed from ${input.fromName} — ${why}`,
    status: 'posted',
  });
  await setAllocations(given.id, [{ budgetItemId: input.toId, amount }], given.version);
}

export function fetchTrashedTransactions(): Promise<{ transactions: Transaction[] }> {
  return request<{ transactions: Transaction[] }>('/transactions', {
    query: { includeDeleted: true, limit: 200 },
  });
}

// ---------------------------------------------------------------------------
// Allocations
// ---------------------------------------------------------------------------

export interface AllocationInput {
  budgetItemId: string;
  amount: number;
  notes?: string | null;
}

export interface SetAllocationsResult {
  allocations: Allocation[];
  transactionVersion: number;
}

/**
 * Replaces every allocation on a transaction.
 *
 * An empty array means "uncategorize". The server does this as one
 * `TransactWrite`, so a split never lands half-applied.
 */
export function setAllocations(
  transactionId: string,
  allocations: AllocationInput[],
  expectedVersion?: number
): Promise<SetAllocationsResult> {
  return request<SetAllocationsResult>('/allocations/set', {
    method: 'POST',
    body: { transactionId, allocations, expectedVersion },
  });
}

// ---------------------------------------------------------------------------
// Paycheck settings
// ---------------------------------------------------------------------------

export interface UpdatePaycheckSettingsInput {
  paycheckAmount?: number;
  envelopeAllocation?: number;
  billAllocation?: number;
  payDays?: number[];
  /** YYYY-MM-DD, or null to clear. */
  rolloverKickoffDate?: string | null;
  expectedVersion: number;
}

export function updatePaycheckSettings(
  input: UpdatePaycheckSettingsInput
): Promise<PaycheckSettings> {
  return request<PaycheckSettings>('/paycheck-settings', { method: 'PUT', body: input });
}

// ---------------------------------------------------------------------------
// Sorting rules
// ---------------------------------------------------------------------------

export function createSortingRule(input: {
  keyword: string;
  budgetItemId: string;
  createdFromDescription?: string | null;
}): Promise<SortingRule> {
  return request<SortingRule>('/sorting-rules', { method: 'POST', body: input });
}

export function updateSortingRule(
  id: string,
  input: { keyword: string; budgetItemId: string }
): Promise<SortingRule> {
  return request<{ sortingRule: SortingRule }>(`/sorting-rules/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: input,
  }).then((payload) => payload.sortingRule);
}

export function deleteSortingRule(id: string): Promise<void> {
  return request<void>(`/sorting-rules/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// Presence (household GPS fixes + shared home pin — view-only on web)
// ---------------------------------------------------------------------------

export interface HomePin {
  latitude: number;
  longitude: number;
}

export interface PresenceResponse {
  presence: Presence[];
  home: HomePin | null;
}

/** Live member locations and the shared home pin. Does not request browser GPS. */
export function fetchPresence(signal?: AbortSignal): Promise<PresenceResponse> {
  return request<PresenceResponse>('/presence', { signal });
}
